import type { FetchInfiniteQueryOptions, FetchQueryOptions } from "@tanstack/react-query";

import { queryClient } from "@/lib/query-client";

/**
 * What `queryOptions(...)` or `infiniteQueryOptions(...)` returns. The data, error and key types are left
 * open on purpose: one call warms reads of different shapes, and nothing here
 * reads the data back.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type PrefetchableQuery =
  | FetchQueryOptions<any, any, any, any>
  | FetchInfiniteQueryOptions<any, any, any, any, any>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type InfiniteQuery = FetchInfiniteQueryOptions<any, any, any, any, any>;

function isInfinite(query: PrefetchableQuery): query is InfiniteQuery {
  return "initialPageParam" in query && query.initialPageParam !== undefined;
}

/**
 * Warms the reads of a route before its component mounts.
 *
 * Call it from the route's `loader`. The router runs a loader on three
 * occasions, and this one line covers all of them:
 * - hover, focus or touch of a `<Link>` (`defaultPreload: "intent"`, after the
 *   router's own 50 ms delay, so sweeping the pointer over a list fetches
 *   nothing for the rows it only crosses);
 * - the real navigation, which starts the fetch before the route renders;
 * - the sidebar, a menu or a button that navigates programmatically.
 *
 * Why it is safe to run often:
 * - `defaultPreloadStaleTime: 0` makes the router re-run the loader on every
 *   hover. That is cheap only because `prefetchQuery` honours the query's
 *   `staleTime`: fresh data (30 s by default) is not fetched again, and a
 *   fetch already in flight is shared, never doubled.
 * - The component's `useQuery` has the same key, so it joins the in-flight
 *   request or reads the cache. No skeleton shows when the data arrived first.
 * - A `queryFn` that reads its own cache (the calendar's `?since=<revision>`
 *   sync) sees whatever the prefetch stored, so incremental sync still works.
 *
 * Infinite queries (`infiniteQueryOptions`) are recognised and warm their first
 * page. It is not awaited on purpose: a slow or failing read must never hold up a
 * navigation. `prefetchQuery` never throws; the component shows the error when
 * it reads the same query. On the server it does nothing: the API uses
 * relative URLs, there is no browser cache to fill, and the render must not
 * wait for data the client will fetch itself.
 */
export function prefetchOnIntent(...queries: PrefetchableQuery[]): void {
  if (import.meta.env.SSR) return;

  for (const query of queries) {
    // An infinite query stores `{ pages, pageParams }`: warming it with
    // `prefetchQuery` would cache the wrong shape under the same key.
    if (isInfinite(query)) void queryClient.prefetchInfiniteQuery(query);
    else void queryClient.prefetchQuery(query);
  }
}
